import { useRef, useCallback, useEffect, useState } from 'react'
import { safeOpenPopupWindow } from '../services/token'
import ReportIssueChip from './ReportIssueChip'
import useDisplayZoom from '../hooks/useDisplayZoom'

// suppressTimerMinimize: pass true on popups that ARE the timer itself
// (so opening the timer doesn't immediately minimize itself).  Every other
// popup dispatches a minimize-time-popup event on mount; Layout.js listens
// and auto-collapses the running timer to its fab when the user opens
// another popup/dialog, so the timer doesn't cover what they want to look at.
// onMinimize: optional separate handler for the "−" button (feedback #52).
// Defaults to onClose when not provided so every popup gets a minimize icon
// next to the × automatically.  The timer popup overrides this to bypass
// its "can't close while running" guard — the minimize button always works.
//
// popupKey + popupRoute: when both are set AND the host is the Electron
// desktop app, an extra ⤴ pop-out button appears in the header.  Clicking
// it spawns a real OS BrowserWindow loading `popupRoute` and closes the
// in-window version.  popupKey is a stable string ("matter-123", "timer")
// that lets Electron dedupe / focus an existing detached window instead
// of opening a duplicate.  In a browser (no electronAPI) the button is
// hidden — feature degrades gracefully.
// positionKey: STABLE localStorage slot for x/y position persistence (feedback
// #514).  Distinct from popupKey (which is used for Electron dedup and changes
// per-instance for e.g. multi-timer windows).  positionKey should be a small
// vocabulary of stable strings — 'timer', 'matter-quickview', 'event-detail'
// etc. — so re-opening the same conceptual popup restores where the user
// dragged it last time.  Falls back to centered layout when omitted.
export default function DraggablePopup({ title, onClose, onMinimize, children, width = 520, suppressTimerMinimize = false, popupKey, popupRoute, positionKey }) {
  const canPopOut = !!(popupKey && popupRoute && window.electronAPI?.openPopupWindow)
  // Position persistence — read/write to localStorage under a namespaced key
  // so multi-window Electron installs share position via the persist:lextrack
  // partition (localStorage is shared once auth is unified — see CLAUDE.md).
  const positionStorageKey = positionKey ? `lextrack_popup_pos:${positionKey}` : null
  const savePosition = useCallback((el) => {
    if (!positionStorageKey || !el) return
    try {
      const left = parseInt(el.style.left, 10)
      const top = parseInt(el.style.top, 10)
      if (Number.isFinite(left) && Number.isFinite(top)) {
        localStorage.setItem(positionStorageKey, JSON.stringify({ left, top, savedAt: Date.now() }))
      }
    } catch { /* quota exceeded or storage disabled — silently no-op */ }
  }, [positionStorageKey])
  const readPosition = useCallback(() => {
    if (!positionStorageKey) return null
    try {
      const raw = localStorage.getItem(positionStorageKey)
      if (!raw) return null
      const p = JSON.parse(raw)
      if (typeof p?.left !== 'number' || typeof p?.top !== 'number') return null
      return { left: p.left, top: p.top }
    } catch { return null }
  }, [positionStorageKey])

  const handlePopOut = async () => {
    // safeOpenPopupWindow runs the sessionStorage → localStorage token
    // migration first so pre-multi-window-deploy users don't get bounced
    // to /login when popping out (feedback #73).
    const p = safeOpenPopupWindow(popupKey, popupRoute, { width, height: 640 })
    if (p) await p
    // Close the in-window version once the OS window has been spawned
    // (or focused if it already existed).  Either way the user wants
    // the popup to live in the detached window from here on.
    if (onClose) onClose()
  }
  const popupRef = useRef(null)
  const dragState = useRef({ dragging: false, ox: 0, oy: 0 })

  // `width` is px at 100 %; the content is rem, so the box follows the
  // automatic zoom (lib/displayZoom.js) or a 2.0 zoom would fit half of it.
  // The OS pop-out (handlePopOut) keeps `width`: pop-outs are never scaled.
  const zoom = useDisplayZoom()
  const boxWidth = Math.round(width * zoom)
  // The initial placement reads it through a ref, so a zoom change on resize
  // re-clamps the popup (below) instead of re-centring it.
  const boxWidthRef = useRef(boxWidth)
  boxWidthRef.current = boxWidth
  const clampedWidth = useRef(null)

  // Clamp requested width to viewport (16px total side gutter) so on
  // iPad split-screen (~375px) the popup doesn't overflow off the right.
  const [effectiveWidth, setEffectiveWidth] = useState(() =>
    Math.min(boxWidth, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 16)
  )
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setEffectiveWidth(Math.min(boxWidth, window.innerWidth - 16))
    }
  }, [boxWidth])

  useEffect(() => {
    if (suppressTimerMinimize) return
    try { window.dispatchEvent(new Event('minimize-time-popup')) } catch {}
  }, [suppressTimerMinimize])

  const onMouseDown = useCallback((e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return
    const el = popupRef.current
    if (!el) return
    dragState.current = { dragging: true, ox: e.clientX - el.offsetLeft, oy: e.clientY - el.offsetTop }
    el.style.cursor = 'grabbing'

    const onMove = (ev) => {
      if (!dragState.current.dragging) return
      // Keep at least 40px of header on-screen in every direction so a
      // popup dragged too far can always be pulled back by its handle.
      const eff = el.offsetWidth || effectiveWidth
      const newLeft = Math.max(-(eff - 40), Math.min(ev.clientX - dragState.current.ox, window.innerWidth - 40))
      const newTop = Math.max(0, Math.min(ev.clientY - dragState.current.oy, window.innerHeight - 40))
      el.style.left = newLeft + 'px'
      el.style.top = newTop + 'px'
    }
    const onUp = () => {
      dragState.current.dragging = false
      el.style.cursor = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      // Feedback #514: persist position after drag ends (not during — avoid
      // localStorage thrash during a rapid drag).  savePosition is a no-op
      // when positionKey is absent.
      savePosition(el)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [effectiveWidth, savePosition])

  // Feedback #514: restore saved position when positionKey is set + valid
  // (within current viewport), otherwise center on mount.  Clamps saved
  // position against current viewport so an offscreen restore (second monitor
  // unplugged) doesn't strand the popup — falls back to centered layout in
  // that case.
  const setRef = useCallback((el) => {
    popupRef.current = el
    if (!el) return
    const eff = Math.min(boxWidthRef.current, window.innerWidth - 16)
    const saved = readPosition()
    const inViewport = (p) => (
      p && p.left >= -(eff - 40) && p.left <= window.innerWidth - 40 &&
      p.top >= 0 && p.top <= window.innerHeight - 40
    )
    let initLeft, initTop
    if (saved && inViewport(saved)) {
      initLeft = saved.left
      initTop = saved.top
    } else {
      initLeft = Math.max(8, Math.min((window.innerWidth - eff) / 2, window.innerWidth - eff - 8))
      initTop = Math.max(8, Math.min(80, window.innerHeight - 200))
    }
    el.style.left = initLeft + 'px'
    el.style.top = initTop + 'px'
  }, [readPosition])

  // Reclamp position + width on window resize / device rotation so a
  // popup opened in landscape doesn't hang off the edge after rotating
  // to portrait (or after a split-screen resize on iPad).
  useEffect(() => {
    const clampToViewport = () => {
      const el = popupRef.current
      if (!el) return
      const eff = Math.min(boxWidth, window.innerWidth - 16)
      setEffectiveWidth(eff)
      el.style.width = eff + 'px'
      const left = parseInt(el.style.left, 10) || 0
      const top = parseInt(el.style.top, 10) || 0
      el.style.left = Math.max(8, Math.min(left, window.innerWidth - eff - 8)) + 'px'
      el.style.top = Math.max(8, Math.min(top, window.innerHeight - 80)) + 'px'
    }
    window.addEventListener('resize', clampToViewport)
    window.addEventListener('orientationchange', clampToViewport)
    // The zoom settles 200 ms after a resize, so a width change from it
    // re-clamps too (not on mount: a saved position stays where it was).
    if (clampedWidth.current !== null && clampedWidth.current !== boxWidth) clampToViewport()
    clampedWidth.current = boxWidth
    return () => {
      window.removeEventListener('resize', clampToViewport)
      window.removeEventListener('orientationchange', clampToViewport)
    }
  }, [boxWidth])

  return (
    <div ref={setRef}
      role="dialog"
      aria-modal="false"
      aria-label={title || 'Popup'}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[85vh] overflow-y-auto"
      style={{ position: 'fixed', width: effectiveWidth, zIndex: 60 }}>
      {/* Drag header */}
      <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl select-none cursor-grab flex items-center justify-between"
        onMouseDown={onMouseDown}>
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <div className="flex items-center gap-2">
          {/* Feedback #511: every popup gets a Report Issue chip in the header
              so users can flag issues without hunting for the sidebar item. */}
          <ReportIssueChip popupTitle={title} />
          {canPopOut && (
            <button onClick={handlePopOut} title="Pop out to its own window"
              aria-label="Pop out popup to its own window"
              className="text-gray-400 hover:text-gray-600 text-base leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200">⤴</button>
          )}
          <button onClick={onMinimize || onClose} title="Minimize"
            aria-label="Minimize popup"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200">−</button>
          <button onClick={onClose} title="Close"
            aria-label="Close popup"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200">&times;</button>
        </div>
      </div>
      {children}
    </div>
  )
}
